import { apiClient } from '@/lib/api/client'

type ApiResponse<T> = { success?: boolean; data?: T; message?: string; meta?: { total?: number; all?: number } }

export type ChecklistTemplateItem = {
  id?: number
  label: string
  sort_order?: number
  required?: boolean
}

export type ChecklistTemplate = {
  id: number
  name: string
  active: boolean
  active_label?: string
  items_count?: number
  items?: ChecklistTemplateItem[]
  created_at?: string
  updated_at?: string
}

export type ChecklistTemplateInput = {
  name: string
  active?: boolean
  items?: ChecklistTemplateItem[]
}

const BASE_PATH = '/service-catalog/checklist-templates'

export const checklistTemplateService = {
  async list(params?: {
    search?: string
    per_page?: number
    active?: boolean
  }): Promise<{ items: ChecklistTemplate[]; total: number }> {
    const res = await apiClient.get<ApiResponse<ChecklistTemplate[]>>(BASE_PATH, { params })
    const data = res.data?.data ?? res.data
    const items = Array.isArray(data) ? data : []
    return {
      items,
      total: res.data?.meta?.total ?? res.data?.meta?.all ?? items.length,
    }
  },

  async get(id: number): Promise<ChecklistTemplate> {
    const res = await apiClient.get(`${BASE_PATH}/${id}`)
    const data = res.data?.data ?? res.data
    if (!data?.id) throw new Error(res.data?.message ?? 'Checklist template not found')
    return data as ChecklistTemplate
  },

  async create(payload: ChecklistTemplateInput): Promise<ChecklistTemplate> {
    const res = await apiClient.post(BASE_PATH, payload)
    const data = res.data?.data ?? res.data
    if (!data) throw new Error(res.data?.message ?? 'Failed to create checklist template')
    return data as ChecklistTemplate
  },

  async update(id: number, payload: ChecklistTemplateInput): Promise<ChecklistTemplate> {
    const res = await apiClient.put(`${BASE_PATH}/${id}`, payload)
    const data = res.data?.data ?? res.data
    if (!data) throw new Error(res.data?.message ?? 'Failed to update checklist template')
    return data as ChecklistTemplate
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`)
  },
}
